import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const chapters = [
  {
    n: "01",
    title: "Assess without assumptions",
    body: "Every engagement starts with evidence. We map your attack surface, systems, and obligations before recommending a single control.",
  },
  {
    n: "02",
    title: "Design for resilience",
    body: "Security that slows teams down gets bypassed. We architect controls that are invisible when they should be and unbreakable when it counts.",
  },
  {
    n: "03",
    title: "Deliver with partners",
    body: "Our vetted ecosystem of independent experts scales precisely to your need — no bloated retainers, no bench you pay to keep warm.",
  },
  {
    n: "04",
    title: "Prove it, continuously",
    body: "Compliance is a byproduct of doing the work well. We leave you audit-ready and measurably more secure than we found you.",
  },
];

export function Approach() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="approach" ref={ref} className="bg-black py-24 lg:py-36 border-t border-white/10" data-testid="approach">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-fog mb-4">The Aliwer method</p>
          <h2 className="font-display text-3xl sm:text-5xl tracking-tight text-paper max-w-[16ch]">
            A manifesto for trustworthy technology.
          </h2>
        </Reveal>

        <div className="mt-16 grid gap-16 lg:grid-cols-[1fr_0.85fr] lg:gap-20 items-start">
          <div className="divide-y divide-white/10">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.05}>
                <div className="py-8 group" data-testid={`chapter-${c.n}`}>
                  <div className="flex items-baseline gap-6">
                    <span className="font-display text-sm text-fog tabular-nums">{c.n}</span>
                    <div>
                      <h3 className="font-display text-2xl lg:text-3xl tracking-tight text-paper group-hover:translate-x-1 transition-transform duration-500">
                        {c.title}
                      </h3>
                      <p className="mt-3 text-sm text-fog leading-relaxed max-w-lg">{c.body}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="relative h-[560px] overflow-hidden rounded-2xl border border-white/10">
            <motion.img
              style={{ y }}
              src="https://static.prod-images.emergentagent.com/jobs/a7857c63-2010-4de3-a354-ef80fc69c639/images/599eb08ab5245f77a531331c218e6d937e7c890ebbcd6451538028d8eda3b9ea.png"
              alt="Geometric structure"
              className="absolute inset-0 w-full h-[120%] object-cover grayscale"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}
